import API from "../../../../api";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Divider,
  Stack,
  Chip,
  Skeleton,
  Tooltip as MuiTooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const EMPTY_PAYLOAD = {
  scans: [],
  cve: [],
  exploit: [],
  malware: [],
  failedAccess: [],
};

const SERIES_LABELS = {
  scans: "Scans",
  cve: "CVE",
  exploit: "Exploit",
  malware: "Malware",
  failedAccess: "Failed Access",
};

const SERIES_ORDER = ["scans", "cve", "exploit", "malware", "failedAccess"];

const getEventTsMs = (e) => {
  const ts =
    e?.end ||
    e?.start ||
    e?.packet_info?.start ||
    e?.log?.[0]?.timeStamp ||
    e?.log?.[0]?.timestamp ||
    e?.timestamp;

  const ms = ts ? dayjs(ts).valueOf() : NaN;
  return Number.isFinite(ms) ? ms : NaN;
};

const getEventWeight = (e) => {
  if (Array.isArray(e?.log)) return e.log.length || 1;
  const c = Number(e?.count);
  return Number.isFinite(c) && c > 0 ? c : 1;
};

const normalizePayload = (data) => {
  if (Array.isArray(data)) {
    return { scans: data, cve: [], exploit: [], malware: [], failedAccess: [] };
  }

  const root = data?.data ?? data ?? {};
  return {
    scans: Array.isArray(root.scans) ? root.scans : [],
    cve: Array.isArray(root.cve) ? root.cve : [],
    exploit: Array.isArray(root.exploit) ? root.exploit : [],
    malware: Array.isArray(root.malware)
      ? root.malware
      : Array.isArray(root.malvare)
      ? root.malvare
      : [],
    failedAccess: Array.isArray(root.failedAccess) ? root.failedAccess : [],
  };
};

const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
};

const getSeverityLabel = (severity) => {
  switch (Number(severity)) {
    case 0:
      return "HIGH";
    case 1:
      return "CRITICAL";
    case 2:
      return "ALERT";
    case 3:
      return "MOVEMENT";
    default:
      return severity === null || severity === undefined || severity === ""
        ? "-"
        : String(severity);
  }
};

const buildIpPort = (ip, port) => {
  const safeIp =
    ip === null || ip === undefined || ip === "" ? "-" : String(ip);
  const safePort =
    port === null || port === undefined || port === "" ? "-" : String(port);

  if (safeIp === "-" && safePort === "-") return "-";
  if (safeIp === "-") return `-:${safePort}`;
  if (safePort === "-") return `${safeIp}:-`;

  return `${safeIp}:${safePort}`;
};

const formatValue = (value, fieldKey, tz, event) => {
  if (fieldKey === "timestamp") {
    if (value === null || value === undefined || value === "") return "-";
    const d = dayjs(value);
    return d.isValid() ? d.tz(tz).format("DD/MM/YYYY HH:mm:ss") : String(value);
  }

  if (fieldKey === "severity") {
    return getSeverityLabel(value);
  }

  if (fieldKey === "source_ip_port") {
    return buildIpPort(event?.src_ip, event?.src_port);
  }

  if (fieldKey === "destination_ip_port") {
    return buildIpPort(event?.dest_ip, event?.dest_port);
  }

  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const DIALOG_FIELDS = [
  { label: "Detail", key: "signature" },
  { label: "Timestamp", key: "timestamp" },
  { label: "Source", key: "source_ip_port" },
  { label: "Destination", key: "destination_ip_port" },
  { label: "Severity", key: "severity" },
  { label: "Source MAC", key: "src_mac" },
  { label: "Destination MAC", key: "dest_mac" },
  { label: "Protocol", key: "proto" },
];

const formatDynamicValue = (key, value, tz) => {
  if (value === null || value === undefined || value === "") return "-";
  if (key === "timestamp" || key === "time" || key === "createdAt") {
    const d = dayjs(value);
    return d.isValid() ? d.tz(tz).format("DD/MM/YYYY HH:mm:ss") : String(value);
  }
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

const DynamicEventDetails = ({ event, tz }) => {
  const entries = Object.entries(event).filter(([key]) => key !== "__v");

  return (
    <Stack spacing={1.2}>
      {entries.map(([key, value]) => (
        <Box
          key={key}
          sx={{
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 2,
            p: 1.2,
            backgroundColor: "rgba(255,255,255,0.02)",
          }}
        >
          <Typography
            sx={{ fontSize: 12, fontWeight: 700, color: "#8fb3ff", mb: 0.5, wordBreak: "break-word" }}
          >
            {key}
          </Typography>
          <Typography
            component="pre"
            sx={{ fontSize: 13, color: "#ddd", wordBreak: "break-word", whiteSpace: "pre-wrap", m: 0, fontFamily: "inherit" }}
          >
            {formatDynamicValue(key, value, tz)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

const EventDetails = ({ event, tz }) => {
  return (
    <Stack spacing={1.2}>
      {DIALOG_FIELDS.map((field) => {
        const rawValue =
          field.key === "source_ip_port" || field.key === "destination_ip_port"
            ? undefined
            : getNestedValue(event, field.key);

        const value = formatValue(rawValue, field.key, tz, event);

        return (
          <Box
            key={field.key}
            sx={{
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 2,
              p: 1.2,
              backgroundColor: "rgba(255,255,255,0.02)",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: "#8fb3ff",
                mb: 0.5,
                wordBreak: "break-word",
              }}
            >
              {field.label}
            </Typography>

            <Typography
              sx={{
                fontSize: 13,
                color: "#ddd",
                wordBreak: "break-word",
              }}
            >
              {value}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

const Scans = ({ height = 260, products = { lecs: true, agent: true } }) => {
  const theme = useTheme();
  const chartRef = useRef(null);

  const [payload, setPayload] = useState(EMPTY_PAYLOAD);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedMeta, setSelectedMeta] = useState({
    seriesKey: "",
    label: "",
    bucketTs: null,
  });

  const tz = window.sessionStorage.getItem("timezone") || "Europe/Rome";

  const showOnlyAgent = !products.lecs && !!products.agent;
  const lecsOnly = !!products.lecs && !products.agent;
  const activeSeriesOrder = useMemo(
    () => (showOnlyAgent ? ["failedAccess"] : SERIES_ORDER),
    [showOnlyAgent]
  );

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    API.getScans()
      .then((response) => {
        if (!mounted) return;
        setPayload(normalizePayload(response.data));
      })
      .catch(() => {
        if (mounted) setPayload(EMPTY_PAYLOAD);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const computed = useMemo(() => {
    const now = dayjs().tz(tz);
    const from = now.subtract(24, "hour");

    const nowMs = now.valueOf();
    const fromMs = from.valueOf();
    const stepMs = 60 * 60 * 1000;

    const start = fromMs - (fromMs % stepMs);
    const end = nowMs - (nowMs % stepMs);

    const keys = [];
    for (let t = start; t <= end; t += stepMs) keys.push(t);

    const makeCountBuckets = () => {
      const m = new Map();
      for (const t of keys) m.set(t, 0);
      return m;
    };

    const makeEventBuckets = () => {
      const m = new Map();
      for (const t of keys) m.set(t, []);
      return m;
    };

    const buckets = {
      scans: makeCountBuckets(),
      cve: makeCountBuckets(),
      exploit: makeCountBuckets(),
      malware: makeCountBuckets(),
      failedAccess: makeCountBuckets(),
    };

    const bucketEvents = {
      scans: makeEventBuckets(),
      cve: makeEventBuckets(),
      exploit: makeEventBuckets(),
      malware: makeEventBuckets(),
      failedAccess: makeEventBuckets(),
    };
    
    const addSeries = (arr, bucketMap, eventMap) => {
      let tot = 0;

      for (const e of arr) {
        const ms = getEventTsMs(e);
        if (!Number.isFinite(ms)) continue;
        if (ms < fromMs || ms > nowMs) continue;

        const b = ms - (ms % stepMs);
        const inc = getEventWeight(e);

        tot += inc;
        bucketMap.set(b, (bucketMap.get(b) ?? 0) + inc);

        const currentEvents = eventMap.get(b) ?? [];
        currentEvents.push(e);
        eventMap.set(b, currentEvents);
      }

      return tot;
    };

    const totals = {
      scans: addSeries(payload.scans, buckets.scans, bucketEvents.scans),
      cve: addSeries(payload.cve, buckets.cve, bucketEvents.cve),
      exploit: addSeries(payload.exploit, buckets.exploit, bucketEvents.exploit),
      malware: addSeries(payload.malware, buckets.malware, bucketEvents.malware),
      failedAccess: addSeries(payload.failedAccess, buckets.failedAccess, bucketEvents.failedAccess),
    };

    const labels = keys.map((t) => dayjs(t).tz(tz).format("HH:00"));

    const seriesValues = {
      scans: keys.map((t) => buckets.scans.get(t) ?? 0),
      cve: keys.map((t) => buckets.cve.get(t) ?? 0),
      exploit: keys.map((t) => buckets.exploit.get(t) ?? 0),
      malware: keys.map((t) => buckets.malware.get(t) ?? 0),
      failedAccess: keys.map((t) => buckets.failedAccess.get(t) ?? 0),
    };

    const eventsBySeriesAndIndex = {
      scans: keys.map((t) => bucketEvents.scans.get(t) ?? []),
      cve: keys.map((t) => bucketEvents.cve.get(t) ?? []),
      exploit: keys.map((t) => bucketEvents.exploit.get(t) ?? []),
      malware: keys.map((t) => bucketEvents.malware.get(t) ?? []),
      failedAccess: keys.map((t) => bucketEvents.failedAccess.get(t) ?? []),
    };

    const grandTotal =
      totals.scans + totals.cve + totals.exploit + totals.malware + totals.failedAccess;

    return {
      keys,
      labels,
      seriesValues,
      totals,
      grandTotal,
      eventsBySeriesAndIndex,
    };
  }, [payload, tz]);

  const chartColors = {
    scans: "#8A7B9E",
    cve: "#B8A84C",
    exploit: "#C49A6C",
    malware: "#B85C5C",
    failedAccess: "#4CA8B8",
  };

  const openBarDialog = useCallback(
    (seriesKey, dataIndex) => {
      const events =
        computed.eventsBySeriesAndIndex?.[seriesKey]?.[dataIndex] ?? [];
      if (!events.length) return;

      setSelectedEvents(events);
      setSelectedIndex(0);
      setSelectedMeta({
        seriesKey,
        label: SERIES_LABELS[seriesKey] || seriesKey,
        bucketTs: computed.keys?.[dataIndex] ?? null,
      });
      setDialogOpen(true);
    },
    [computed]
  );

  const handleChartClick = useCallback(
    (event) => {
      const chart = chartRef.current;
      if (!chart) return;

      const nativeEvent = event?.nativeEvent ?? event;
      const points = chart.getElementsAtEventForMode(
        nativeEvent,
        "nearest",
        { intersect: true },
        true
      );

      if (!points?.length) return;

      const firstPoint = points[0];
      const datasetIndex = firstPoint.datasetIndex;
      const dataIndex = firstPoint.index;
      const seriesKey = activeSeriesOrder[datasetIndex];

      if (!seriesKey) return;
      if (lecsOnly && seriesKey === "failedAccess") return;

      openBarDialog(seriesKey, dataIndex);
    },
    [openBarDialog, activeSeriesOrder, lecsOnly]
  );

  const data = useMemo(() => {
    const faColor = lecsOnly ? "rgba(120,120,120,0.3)" : chartColors.failedAccess;

    const allDatasets = [
      { label: "Scans", data: computed.seriesValues.scans, backgroundColor: chartColors.scans, borderWidth: 0 },
      { label: "CVE", data: computed.seriesValues.cve, backgroundColor: chartColors.cve, borderWidth: 0 },
      { label: "Exploit", data: computed.seriesValues.exploit, backgroundColor: chartColors.exploit, borderWidth: 0 },
      { label: "Malware", data: computed.seriesValues.malware, backgroundColor: chartColors.malware, borderWidth: 0 },
      { label: "Failed Access", data: computed.seriesValues.failedAccess, backgroundColor: faColor, borderWidth: 0 },
    ];

    return {
      labels: computed.labels,
      datasets: showOnlyAgent ? [allDatasets[4]] : allDatasets,
    };
  }, [computed.labels, computed.seriesValues, showOnlyAgent, lecsOnly, chartColors.cve, chartColors.exploit, chartColors.failedAccess, chartColors.malware, chartColors.scans]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onHover: (event, elements, chart) => {
        const target = chart?.canvas;
        if (target) {
          if (elements?.length) {
            const sk = activeSeriesOrder[elements[0].datasetIndex];
            target.style.cursor = lecsOnly && sk === "failedAccess" ? "not-allowed" : "pointer";
          } else {
            target.style.cursor = "default";
          }
        }
      },
      plugins: {
        legend: {
          display: true,
        },
        tooltip: {
          enabled: true,
          callbacks: {
            afterLabel: (context) => {
              const seriesKey = activeSeriesOrder[context.datasetIndex];
              const events =
                computed.eventsBySeriesAndIndex?.[seriesKey]?.[
                  context.dataIndex
                ] ?? [];
              return `Events: ${events.length}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: false,
          grid: { display: false },
          ticks: { color: theme.palette.text.secondary },
        },
        y: {
          stacked: false,
          beginAtZero: true,
          ticks: {
            precision: 0,
            color: theme.palette.text.secondary,
          },
          grid: {
            color: "rgba(255,255,255,0.08)",
          },
        },
      },
    }),
    [computed.eventsBySeriesAndIndex, theme.palette.text.secondary, activeSeriesOrder, lecsOnly]
  );

  const relevantTotal = showOnlyAgent ? computed.totals.failedAccess : computed.grandTotal;
  const noData = !loading && relevantTotal <= 0;
  const currentEvent = selectedEvents[selectedIndex] ?? null;
  const canGoPrev = selectedIndex > 0;
  const canGoNext = selectedIndex < selectedEvents.length - 1;

  const handlePrev = () => {
    if (!canGoPrev) return;
    setSelectedIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (!canGoNext) return;
    setSelectedIndex((prev) => prev + 1);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEvents([]);
    setSelectedIndex(0);
    setSelectedMeta({
      seriesKey: "",
      label: "",
      bucketTs: null,
    });
  };

  return (
    <>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2, boxSizing: "border-box", background: '#1e1e1e' }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>Security events last 24 hours</Typography>
            <MuiTooltip title="Click on a bar to open the events of that hour and category">
              <InfoOutlinedIcon sx={{ fontSize: 14, color: "#555" }} />
            </MuiTooltip>
          </Box>

          <Box component="span" sx={{ fontSize: 11, color: "#555" }}>
            {loading
              ? ""
              : showOnlyAgent
              ? `Total: ${computed.totals.failedAccess} (FA:${computed.totals.failedAccess})`
              : lecsOnly
              ? `Total: ${computed.totals.scans + computed.totals.cve + computed.totals.exploit + computed.totals.malware} (S:${computed.totals.scans} C:${computed.totals.cve} E:${computed.totals.exploit} M:${computed.totals.malware})`
              : `Total: ${computed.grandTotal} (S:${computed.totals.scans} C:${computed.totals.cve} E:${computed.totals.exploit} M:${computed.totals.malware} FA:${computed.totals.failedAccess})`}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end", gap: "6px", px: 1, pb: 1 }}>
            {[55, 30, 70, 45, 80, 35, 60, 25, 90, 40, 65, 50].map((h, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                sx={{ flex: 1, height: `${h}%`, bgcolor: "rgba(255,255,255,0.07)", borderRadius: "4px 4px 0 0" }}
              />
            ))}
          </Box>
        ) : noData ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.8 }}>
            {showOnlyAgent
              ? "There are no failed access events in the last 24 hours"
              : "There are no scans/cve/exploit/malware/failed access in the last 24 hours"}
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
            <Bar
              ref={chartRef}
              data={data}
              options={options}
              onClick={handleChartClick}
            />
          </Box>
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
              {selectedMeta.label || "Event details"}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label={
                  selectedMeta.bucketTs
                    ? dayjs(selectedMeta.bucketTs).tz(tz).format("DD/MM/YYYY HH:00")
                    : "-"
                }
              />
              <Chip
                size="small"
                label={`Event ${selectedEvents.length ? selectedIndex + 1 : 0}/${selectedEvents.length}`}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton onClick={handlePrev} disabled={!canGoPrev}>
              <ChevronLeftIcon />
            </IconButton>

            <IconButton onClick={handleNext} disabled={!canGoNext}>
              <ChevronRightIcon />
            </IconButton>

            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          {currentEvent ? (
            selectedMeta.seriesKey === "failedAccess" ? (
              <DynamicEventDetails event={currentEvent} tz={tz} />
            ) : (
              <EventDetails event={currentEvent} tz={tz} />
            )
          ) : (
            <Box sx={{ py: 3, textAlign: "center", opacity: 0.8 }}>
              No event selected
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Scans;